import { ESessionStorageKeys } from 'constants/enums';
import { webStorageUtil } from 'state/webStorage.state.service';
import { urlHasLandingAttribution, urlIsEclLinkoutLanding } from './landingUrlAttribution';

export const LANDING_URL_MAX_LENGTH = 2000;

const readOptOut = (): boolean => {
  try {
    return webStorageUtil.get(ESessionStorageKeys.LANDING_URL_OPT_OUT) === '1';
  } catch {
    return false;
  }
};

const setOptOut = (optOut: boolean): void => {
  try {
    if (optOut) {
      webStorageUtil.set(ESessionStorageKeys.LANDING_URL_OPT_OUT, '1');
    } else {
      webStorageUtil.remove(ESessionStorageKeys.LANDING_URL_OPT_OUT);
    }
  } catch {
    /* best effort */
  }
};

const readStoredLandingUrl = (): string | null => {
  try {
    const stored = webStorageUtil.get(ESessionStorageKeys.LANDING_URL);
    return typeof stored === 'string' ? stored : null;
  } catch {
    return null;
  }
};

const writeLandingUrl = (href: string): void => {
  webStorageUtil.set(ESessionStorageKeys.LANDING_URL, href.slice(0, LANDING_URL_MAX_LENGTH));
};

const buildCapturableHref = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const url = new URL(window.location.href);
  if (!urlHasLandingAttribution(url)) {
    return null;
  }
  return url.href.slice(0, LANDING_URL_MAX_LENGTH);
};

export const clearLandingUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    webStorageUtil.remove(ESessionStorageKeys.LANDING_URL);
    setOptOut(true);
  } catch {
    /* sessionStorage unavailable — best effort */
  }
};

export const captureLandingUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const capturable = buildCapturableHref();
    if (!capturable) {
      return;
    }

    const stored = readStoredLandingUrl();
    const optedOut = readOptOut();

    if (optedOut) {
      if (!urlIsEclLinkoutLanding(new URL(capturable))) {
        return;
      }
      setOptOut(false);
      writeLandingUrl(capturable);
      return;
    }

    if (stored) {
      if (stored === capturable) {
        return;
      }
      const nextUrl = new URL(capturable);
      if (urlIsEclLinkoutLanding(nextUrl) && urlIsEclLinkoutLanding(new URL(stored))) {
        writeLandingUrl(capturable);
      }
      return;
    }

    writeLandingUrl(capturable);
  } catch {
    /* sessionStorage unavailable — best effort */
  }
};

export const getLandingUrl = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = readStoredLandingUrl();
    if (!stored) {
      return null;
    }
    if (!urlHasLandingAttribution(new URL(stored))) {
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};
